import { useEffect, useRef, useState } from 'react';
import {
  getLoanEmailHistory,
  type HistoryQuery,
  type HistoryResponse
} from '@/services/loanEmailHistoryService';
import { createComponentLogger } from '@/utils/logger';
const log = createComponentLogger('LoanEmailHistory', 'loan-email-history');

/** The caller keys the workspace by loan and filters; cursors belong to that query. */
export function useHistoryPage(query: HistoryQuery) {
  const [cursors, setCursors] = useState<NonNullable<HistoryQuery['eventCursor']>[]>([]);
  const [revision, setRevision] = useState(0);
  const [data, setData] = useState<HistoryResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const generation = useRef(0);
  const queryKey = JSON.stringify({
    ...query,
    [query.action === 'detail' ? 'eventCursor' : 'cursor']: cursors.at(-1) ?? null
  });
  useEffect(() => {
    let active = true;
    const request = ++generation.current;
    setLoading(true);
    setError(null);
    setData(null);
    void getLoanEmailHistory(JSON.parse(queryKey)).then((result) => {
      if (active && request === generation.current) {
        setData(result);
      }
    }).catch((cause: unknown) => {
      if (!active || request !== generation.current) {
        return;
      }
      // Provider errors can contain addresses or stored bodies; log only the operation.
      log.error('Email history request failed', { operation: query.action });
      setError(
        cause instanceof Error ? cause.message : 'Email history is unavailable. Refresh to try again.'
      );
    }).finally(() => {
      if (active && request === generation.current) {
        setLoading(false);
      }
    });
    return () => {
      active = false;
    };
  }, [queryKey, revision, query.action]);
  const next = query.action === 'detail' ? data?.items[0]?.nextEventCursor : data?.nextCursor;
  const change = (fn: () => void) => {
    generation.current++;
    setLoading(true);
    setData(null);
    setError(null);
    fn();
  };
  return {
    data,
    error,
    loading,
    page: cursors.length + 1,
    hasNext: Boolean(next),
    refresh: () =>
      change(() => {
        setCursors([]);
        setRevision((value) => value + 1);
      }),
    previous: () => change(() => setCursors((value) => value.slice(0, -1))),
    next: () => {
      if (next) {
        change(() => setCursors((value) => [...value, next]));
      }
    }
  };
}
